import React, { useState } from "react";
import * as stock from "../../../services/inventoryStockService";
import { DataTable, act, hint, linkBtn } from "../../Fin/gl/glUi";
import { EmptyState, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, formatDate, inputStyle, pageStyle, useLoad } from "../../../components/ui";
import { ItemPicker, SupplierSelect, TrackingInput, WarehouseSelect, cleanDetails, fmtAmt, fmtQty, useInvSettings } from "./invUi";

// Inventory → Suppliers, Purchase Orders and Goods Receipts.
//   PO: draft → submit (approved at once below the limit, else waits for the owner / an admin) →
//   goods received (partly or all) → closed. A receipt can also come without an order (when allowed).
//   On a receipt: landed cost (freight, duty ...) and returns to the supplier. Payables matches the
//   supplier's bill to the receipt lines.

const PO_STATUS = { DRAFT: "Draft", PENDING_APPROVAL: "Waiting for approval", APPROVED: "Approved", PARTIALLY_RECEIVED: "Partly received", RECEIVED: "Received", CLOSED: "Closed", CANCELLED: "Cancelled" };
const statusChip = (st) => (
  <span style={{ fontSize: 12, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: st === "PENDING_APPROVAL" ? "#FEF3C7" : ["APPROVED", "PARTIALLY_RECEIVED"].includes(st) ? "#DBEAFE" : st === "RECEIVED" ? "#D1FAE5" : "#F3F4F6", color: "#374151" }}>
    {PO_STATUS[st] || st}
  </span>
);

// ---------- suppliers ----------

export function Suppliers() {
  const { data: s } = useInvSettings();
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useLoad(() => stock.listSuppliers({ q: q || undefined }), [q]);
  const [editing, setEditing] = useState(null);
  return (
    <div style={pageStyle}>
      <PageHeader title="Suppliers" subtitle="One list for purchasing and Payables" actions={s && s.can.purchase ? <PrimaryButton onClick={() => setEditing({})}>Add supplier</PrimaryButton> : null} />
      <input className="form-control mb-2" style={{ ...inputStyle, maxWidth: 300 }} placeholder="Search" aria-label="Search" value={q} onChange={(e) => setQ(e.target.value)} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={s && s.can.manage ? (r) => setEditing(r) : undefined}
          empty={<EmptyState title="No suppliers yet" />}
          columns={[
            { label: "Code", render: (r) => <b>{r.code}</b> },
            { label: "Name", render: (r) => r.name },
            { label: "Phone", render: (r) => r.phone || "" },
            { label: "Currency", render: (r) => r.currency || "" },
            { label: "Lead time", num: true, render: (r) => (r.lead_time_days ? `${r.lead_time_days} d` : "") },
            { label: "Items", num: true, render: (r) => r.items },
            { label: "In Payables", render: (r) => r.fin_supplier_code || "" },
            { label: "Status", render: (r) => r.status.toLowerCase() },
          ]}
        />
      </LoadState>
      <FormDrawer
        show={!!editing}
        title={editing && editing.id ? `Supplier ${editing.code}` : "Add supplier"}
        initial={editing ? { code: editing.code || "", name: editing.name || "", contact_name: editing.contact_name || "", phone: editing.phone || "", email: editing.email || "", trn: editing.trn || "", address: editing.address || "", country_code: editing.country_code || "", currency: editing.currency || "", lead_time_days: editing.lead_time_days || "", status: editing.status || "ACTIVE" } : {}}
        onHide={() => setEditing(null)}
        onDone={reload}
        onSave={(clean) => stock.saveSupplier(editing && editing.id, clean)}
        fields={[
          ...(editing && editing.id ? [] : [{ key: "code", label: "Code", max: 30, hint: "Left empty: SUP-0001, SUP-0002 ..." }]),
          { key: "name", label: "Name", required: true, max: 200 },
          { key: "contact_name", label: "Contact", max: 150 },
          { key: "phone", label: "Phone", max: 30 },
          { key: "email", label: "Email", max: 150 },
          { key: "trn", label: "Tax number (TRN / GSTIN)", max: 32 },
          { key: "address", label: "Address", type: "textarea", rows: 2 },
          { key: "country_code", label: "Country (2 letters)", max: 2 },
          { key: "currency", label: "Currency (3 letters)", max: 3 },
          { key: "lead_time_days", label: "Usual delivery time (days)", type: "number" },
          ...(editing && editing.id ? [{ key: "status", label: "Status", type: "select", options: [{ label: "Active", value: "ACTIVE" }, { label: "Inactive", value: "INACTIVE" }] }] : []),
        ]}
      />
    </div>
  );
}

// ---------- purchase orders ----------

function PoEditor({ po = null, onHide, onDone }) {
  const [h, setH] = useState({ supplier_id: po ? po.supplier_id : null, warehouse_id: po ? po.warehouse_id : null, expected_date: po && po.expected_date ? String(po.expected_date).slice(0, 10) : "", supplier_reference: po ? po.supplier_reference || "" : "", is_urgent: po ? !!po.is_urgent : false, remarks: po ? po.remarks || "" : "" });
  const [lines, setLines] = useState(po ? po.lines.filter((l) => l.line_kind === "ITEM").map((l) => ({ item: { id: l.item_id, sku: l.sku, name: l.description, purchase_uom: l.purchase_uom, purchase_uom_factor: l.uom_factor }, quantity: l.quantity, unit_cost: l.unit_cost, use_uom: !!l.purchase_uom })) : [{ item: null, quantity: "", unit_cost: "", use_uom: true }]);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const total = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_cost || 0), 0);
  const save = async () => {
    setBusy(true);
    const body = { ...h, expected_date: h.expected_date || undefined, lines: lines.filter((l) => l.item).map((l) => ({ item_id: l.item.id, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost), purchase_uom: l.use_uom && l.item.purchase_uom ? l.item.purchase_uom : null })) };
    const res = await act(() => stock.savePurchaseOrder(po && po.id, body));
    setBusy(false);
    if (res) {
      onDone(res.data && res.data.id);
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={() => !busy && onHide()} title={po ? `Purchase order ${po.po_number}` : "New purchase order"} width={900} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Save draft</PrimaryButton></>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "8px 12px" }}>
        <div>
          <label className="form-label" htmlFor="po-sup">Supplier</label>
          <SupplierSelect id="po-sup" value={h.supplier_id} onChange={(v) => setH({ ...h, supplier_id: v })} />
        </div>
        <div>
          <label className="form-label" htmlFor="po-wh">Deliver to</label>
          <WarehouseSelect id="po-wh" value={h.warehouse_id} onChange={(v) => setH({ ...h, warehouse_id: v })} placeholder="Default warehouse" />
        </div>
        <div>
          <label className="form-label" htmlFor="po-exp">Expected</label>
          <input id="po-exp" type="date" className="form-control" style={inputStyle} value={h.expected_date} onChange={(e) => setH({ ...h, expected_date: e.target.value })} />
        </div>
        <div>
          <label className="form-label" htmlFor="po-ref">Supplier's quote / reference</label>
          <input id="po-ref" className="form-control" style={inputStyle} value={h.supplier_reference} onChange={(e) => setH({ ...h, supplier_reference: e.target.value })} />
        </div>
      </div>
      <label className="d-flex align-items-center gap-2 my-2" style={{ fontSize: 14 }}>
        <input type="checkbox" checked={h.is_urgent} onChange={(e) => setH({ ...h, is_urgent: e.target.checked })} /> Urgent (vehicle off road / emergency order)
      </label>
      <h6 className="mt-2">Lines</h6>
      {lines.map((l, i) => (
        <div key={i} className="d-flex flex-wrap gap-2 align-items-center mb-2">
          <div style={{ flex: 1, minWidth: 280 }}>
            <ItemPicker id={`po-it-${i}`} value={l.item ? l.item.id : null} onChange={(item) => set(i, { item, use_uom: !!(item && item.purchase_uom) })} />
          </div>
          <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 100 }} placeholder="Qty" aria-label={`Quantity line ${i + 1}`} value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
          {l.item && l.item.purchase_uom ? (
            <select className="form-select" style={{ width: 150 }} aria-label="Unit" value={l.use_uom ? "P" : "S"} onChange={(e) => set(i, { use_uom: e.target.value === "P" })}>
              <option value="P">
                {l.item.purchase_uom} (×{fmtQty(l.item.purchase_uom_factor)})
              </option>
              <option value="S">{l.item.unit_of_measure || "unit"}</option>
            </select>
          ) : null}
          <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 120 }} placeholder="Unit cost" aria-label={`Unit cost line ${i + 1}`} value={l.unit_cost} onChange={(e) => set(i, { unit_cost: e.target.value })} />
          <span style={{ width: 100, textAlign: "right", fontSize: 14 }}>{fmtAmt(Number(l.quantity || 0) * Number(l.unit_cost || 0))}</span>
          {lines.length > 1 ? (
            <button type="button" style={linkBtn} onClick={() => setLines(lines.filter((_, k) => k !== i))}>
              Remove
            </button>
          ) : null}
        </div>
      ))}
      <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setLines([...lines, { item: null, quantity: "", unit_cost: "", use_uom: true }])}>
        Add line
      </button>
      <div style={{ textAlign: "right", fontWeight: 700, marginTop: 8 }}>Total {fmtAmt(total)}</div>
      <textarea className="form-control mt-2" rows={2} placeholder="Remarks" aria-label="Remarks" value={h.remarks} onChange={(e) => setH({ ...h, remarks: e.target.value })} />
      {po && po.lines.some((l) => l.line_kind === "EXT") ? <div style={{ ...hint, marginTop: 6 }}>Vehicle lines are kept as they are (Dealership → Purchasing).</div> : null}
    </SlidePanel>
  );
}

// Receive goods: against an order (its open lines) or direct from a supplier.
export function ReceivePanel({ po = null, onHide, onDone }) {
  const open = po ? po.lines.filter((l) => l.line_kind === "ITEM" && l.status === "OPEN" && Number(l.quantity) > Number(l.qty_received)) : [];
  const [h, setH] = useState({ supplier_id: null, warehouse_id: po ? po.warehouse_id : null, supplier_delivery_note: "", receipt_date: "" });
  const [rows, setRows] = useState(po ? open.map((l) => ({ po_line_id: l.id, item: { id: l.item_id, sku: l.sku, name: l.description, tracking: l.tracking, unit_of_measure: l.unit_of_measure }, factor: Number(l.uom_factor || 1), unit: l.purchase_uom || l.unit_of_measure, open: Number(l.quantity) - Number(l.qty_received), quantity: Number(l.quantity) - Number(l.qty_received), details: [], bin_location: "" })) : [{ item: null, quantity: "", unit_cost: "", factor: 1, details: [], bin_location: "" }]);
  const [busy, setBusy] = useState(false);
  const set = (i, patch) => setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  const save = async () => {
    setBusy(true);
    const lines = rows
      .filter((r) => (po ? Number(r.quantity) > 0 : r.item))
      .map((r) => (po ? { po_line_id: r.po_line_id, quantity: Number(r.quantity), details: cleanDetails(r.details), bin_location: r.bin_location || undefined } : { item_id: r.item.id, quantity: Number(r.quantity), unit_cost: Number(r.unit_cost), details: cleanDetails(r.details), bin_location: r.bin_location || undefined }));
    const res = await act(() => stock.receiveGoods({ ...h, po_id: po ? po.id : undefined, supplier_id: po ? undefined : h.supplier_id, receipt_date: h.receipt_date || undefined, lines }));
    setBusy(false);
    if (res) {
      onDone(res.data && res.data.id);
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={() => !busy && onHide()} title={po ? `Receive goods on ${po.po_number}` : "Receive goods without an order"} width={940} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Post goods receipt</PrimaryButton></>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "8px 12px" }} className="mb-2">
        {po ? null : (
          <div>
            <label className="form-label" htmlFor="gr-sup">Supplier</label>
            <SupplierSelect id="gr-sup" value={h.supplier_id} onChange={(v) => setH({ ...h, supplier_id: v })} />
          </div>
        )}
        <div>
          <label className="form-label" htmlFor="gr-wh">Into warehouse</label>
          <WarehouseSelect id="gr-wh" value={h.warehouse_id} onChange={(v) => setH({ ...h, warehouse_id: v })} placeholder="Default warehouse" />
        </div>
        <div>
          <label className="form-label" htmlFor="gr-dn">Supplier's delivery note</label>
          <input id="gr-dn" className="form-control" style={inputStyle} value={h.supplier_delivery_note} onChange={(e) => setH({ ...h, supplier_delivery_note: e.target.value })} />
        </div>
        <div>
          <label className="form-label" htmlFor="gr-date">Received on</label>
          <input id="gr-date" type="date" className="form-control" style={inputStyle} value={h.receipt_date} onChange={(e) => setH({ ...h, receipt_date: e.target.value })} />
        </div>
      </div>
      {rows.map((r, i) => (
        <div key={i} className="mb-2 pb-2" style={{ borderBottom: "1px solid #F3F4F6" }}>
          <div className="d-flex flex-wrap gap-2 align-items-center">
            <div style={{ flex: 1, minWidth: 260 }}>{po ? <span><b>{r.item.sku}</b> {r.item.name}<span style={hint}> · {fmtQty(r.open)} {r.unit} open</span></span> : <ItemPicker id={`gr-it-${i}`} value={r.item ? r.item.id : null} onChange={(item) => set(i, { item, details: [] })} />}</div>
            <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 100 }} placeholder="Qty" aria-label={`Quantity received line ${i + 1}`} value={r.quantity} onChange={(e) => set(i, { quantity: e.target.value })} />
            {po ? null : <input type="number" min={0} step="any" className="form-control" style={{ ...inputStyle, width: 120 }} placeholder="Unit cost" aria-label={`Unit cost line ${i + 1}`} value={r.unit_cost} onChange={(e) => set(i, { unit_cost: e.target.value })} />}
            <input className="form-control" style={{ ...inputStyle, width: 120 }} placeholder="Bin" aria-label={`Bin line ${i + 1}`} value={r.bin_location} onChange={(e) => set(i, { bin_location: e.target.value })} />
          </div>
          <TrackingInput item={r.item} qty={Number(r.quantity || 0) * (r.factor || 1)} value={r.details} onChange={(d) => set(i, { details: d })} mode="in" />
        </div>
      ))}
      {po ? null : (
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setRows([...rows, { item: null, quantity: "", unit_cost: "", factor: 1, details: [], bin_location: "" }])}>
          Add line
        </button>
      )}
      {po && !open.length ? <EmptyState title="Nothing left to receive on this order" /> : null}
      <div style={{ ...hint, marginTop: 6 }}>Quantities are in the order's unit (boxes, drums ...); stock is kept in the item's own unit. Batch items need the batch and expiry; serial items one serial per unit.</div>
    </SlidePanel>
  );
}

function PoView({ id, s, onHide, onChanged }) {
  const { data: po, loading, error, reload } = useLoad(() => stock.getPurchaseOrder(id), [id]);
  const [editing, setEditing] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const refresh = () => {
    reload();
    onChanged();
  };
  const doAct = async (a, body) => {
    if (await act(() => stock.purchaseOrderAction(id, a, body))) refresh();
  };
  return (
    <SlidePanel show onHide={onHide} title={po ? `Purchase order ${po.po_number}` : "Purchase order"} width={960}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {po ? (
          <>
            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
              {statusChip(po.status)}
              {po.is_urgent ? <span style={{ fontSize: 12, fontWeight: 700, color: "#B91C1C" }}>URGENT</span> : null}
              <span style={hint}>
                {po.supplier_name} · {formatDate(po.order_date)}
                {po.expected_date ? ` · expected ${formatDate(po.expected_date)}` : ""} · {po.warehouse_code || ""} · {po.currency} {fmtAmt(po.total)}
              </span>
              {po.status === "DRAFT" && s.can.purchase && !po.lines.some((l) => l.line_kind === "EXT") ? <SecondaryButton onClick={() => setEditing(true)}>Edit</SecondaryButton> : null}
              {po.status === "DRAFT" && s.can.purchase ? <PrimaryButton onClick={() => doAct("submit")}>Submit</PrimaryButton> : null}
              {po.status === "PENDING_APPROVAL" && s.can.approve ? <PrimaryButton onClick={() => doAct("approve")}>Approve</PrimaryButton> : null}
              {po.status === "PENDING_APPROVAL" && s.can.approve ? <SecondaryButton onClick={() => doAct("reject", { note: window.prompt("Why is it returned?") || "" })}>Return to the buyer</SecondaryButton> : null}
              {["APPROVED", "PARTIALLY_RECEIVED"].includes(po.status) && s.can.purchase && po.lines.some((l) => l.line_kind === "ITEM") ? <PrimaryButton onClick={() => setReceiving(true)}>Receive goods</PrimaryButton> : null}
              {["DRAFT", "PENDING_APPROVAL", "APPROVED"].includes(po.status) && s.can.purchase ? <SecondaryButton onClick={() => doAct("cancel")}>Cancel order</SecondaryButton> : null}
              {["PARTIALLY_RECEIVED"].includes(po.status) && s.can.purchase ? <SecondaryButton onClick={() => doAct("close")}>Close (no more to come)</SecondaryButton> : null}
            </div>
            <DataTable
              rows={po.lines}
              columns={[
                { label: "#", render: (l) => l.line_no },
                { label: "Item", render: (l) => (l.line_kind === "EXT" ? `${l.description} (vehicle)` : `${l.sku || ""} ${l.description}`) },
                { label: "Qty", num: true, render: (l) => `${fmtQty(l.quantity)} ${l.purchase_uom || l.unit_of_measure || ""}` },
                { label: "Unit cost", num: true, render: (l) => fmtAmt(l.unit_cost) },
                { label: "Received", num: true, render: (l) => fmtQty(l.qty_received) },
                { label: "Billed", num: true, render: (l) => fmtQty(l.qty_billed) },
                { label: "Returned", num: true, render: (l) => (l.qty_returned ? fmtQty(l.qty_returned) : "") },
              ]}
            />
            <h6 className="mt-3">Goods receipts</h6>
            {po.receipts.length ? (
              po.receipts.map((g) => (
                <div key={g.id} style={{ fontSize: 14 }} className="mb-2">
                  <b>{g.grn_number}</b> · {formatDate(g.receipt_date)} {g.supplier_delivery_note ? `· DN ${g.supplier_delivery_note}` : ""}
                  <div style={hint}>{g.lines.map((l) => `${l.description} ×${fmtQty(l.quantity)}`).join(" · ")}</div>
                </div>
              ))
            ) : (
              <div style={hint}>Nothing received yet.</div>
            )}
            {po.remarks ? <div style={{ ...hint, marginTop: 8, whiteSpace: "pre-wrap" }}>{po.remarks}</div> : null}
          </>
        ) : null}
      </LoadState>
      {editing && po ? <PoEditor po={po} onHide={() => setEditing(false)} onDone={refresh} /> : null}
      {receiving && po ? <ReceivePanel po={po} onHide={() => setReceiving(false)} onDone={refresh} /> : null}
    </SlidePanel>
  );
}

export function PurchaseOrders() {
  const { data: s } = useInvSettings();
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useLoad(() => stock.listPurchaseOrders({ status: status || undefined, q: q || undefined }), [status, q]);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(null);
  return (
    <div style={pageStyle}>
      <PageHeader title="Purchase Orders" subtitle="Orders to suppliers, approved and received" actions={s && s.can.purchase && s.purchasing_enabled ? <PrimaryButton onClick={() => setCreating(true)}>New purchase order</PrimaryButton> : null} />
      {s && !s.purchasing_enabled ? <div className="mb-2" style={{ ...hint, background: "#FEF3C7", padding: "8px 12px", borderRadius: 6 }}>Purchasing is off. Switch on "Use purchasing" in Inventory Settings.</div> : null}
      <div className="d-flex flex-wrap gap-2 mb-2">
        <select className="form-select" style={{ width: 220 }} aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All</option>
          {Object.entries(PO_STATUS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <input className="form-control" style={{ ...inputStyle, maxWidth: 280 }} placeholder="Search order, supplier, reference" aria-label="Search" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No purchase orders" />}
          columns={[
            { label: "Order", render: (r) => <b>{r.po_number}</b> },
            { label: "Supplier", render: (r) => r.supplier_name },
            { label: "Date", render: (r) => formatDate(r.order_date) },
            { label: "Expected", render: (r) => (r.expected_date ? formatDate(r.expected_date) : "") },
            { label: "Total", num: true, render: (r) => `${r.currency || ""} ${fmtAmt(r.total)}` },
            { label: "", render: (r) => (r.is_urgent ? <span style={{ color: "#B91C1C", fontWeight: 700, fontSize: 12 }}>URGENT</span> : r.source === "REORDER" ? <span style={hint}>reorder</span> : null) },
            { label: "Status", render: (r) => statusChip(r.status) },
          ]}
        />
      </LoadState>
      {creating ? <PoEditor onHide={() => setCreating(false)} onDone={(id) => { reload(); if (id) setOpen(id); }} /> : null}
      {open && s ? <PoView id={open} s={s} onHide={() => setOpen(null)} onChanged={reload} /> : null}
    </div>
  );
}

// ---------- goods receipts ----------

function ReceiptView({ id, s, onHide, onChanged }) {
  const { data: g, loading, error, reload } = useLoad(() => stock.getReceipt(id), [id]);
  const [landed, setLanded] = useState(false);
  const [returning, setReturning] = useState(false);
  const [ret, setRet] = useState({});
  const [reason, setReason] = useState("");
  const refresh = () => {
    reload();
    onChanged();
  };
  const postReturn = async () => {
    const lines = Object.entries(ret).filter(([, q]) => Number(q) > 0).map(([lineId, q]) => ({ receipt_line_id: Number(lineId), quantity: Number(q) }));
    const res = await act(() => stock.returnToSupplier(id, { reason, lines }));
    if (res) {
      setReturning(false);
      setRet({});
      setReason("");
      refresh();
    }
  };
  return (
    <SlidePanel show onHide={onHide} title={g ? `Goods receipt ${g.grn_number}` : "Goods receipt"} width={960}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {g ? (
          <>
            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
              <span style={hint}>
                {g.supplier_name} · {formatDate(g.receipt_date)} {g.po_number ? `· order ${g.po_number}` : "· no order"} {g.supplier_delivery_note ? `· DN ${g.supplier_delivery_note}` : ""} · {g.warehouse_code || ""}
                {g.accounting_event_id ? " · booked" : ""}
              </span>
              {s.can.purchase ? <SecondaryButton onClick={() => setLanded(true)}>Add landed cost</SecondaryButton> : null}
              {s.can.purchase && g.lines.some((l) => l.line_kind === "ITEM") ? <SecondaryButton onClick={() => setReturning(!returning)}>{returning ? "Close return" : "Return to supplier"}</SecondaryButton> : null}
            </div>
            <DataTable
              rows={g.lines}
              columns={[
                { label: "Item", render: (l) => l.description },
                { label: "Received", num: true, render: (l) => `${fmtQty(l.quantity)}${l.stock_qty !== l.quantity ? ` (${fmtQty(l.stock_qty)} ${l.unit_of_measure || ""})` : ""}` },
                { label: "Unit cost", num: true, render: (l) => fmtAmt(l.unit_cost) },
                { label: "Landed", num: true, render: (l) => (l.landed_cost ? fmtAmt(l.landed_cost) : "") },
                { label: "Batches / serials", render: (l) => (l.tracking || []).map((d) => d.serial_no || `${d.lot_no} ×${fmtQty(d.qty)}`).join(", ") },
                { label: "Billed", num: true, render: (l) => fmtQty(l.qty_billed) },
                { label: "Returned", num: true, render: (l) => (l.qty_returned ? fmtQty(l.qty_returned) : "") },
                ...(returning
                  ? [{ label: "Send back", render: (l) => (l.line_kind === "ITEM" ? <input type="number" min={0} step="any" className="form-control form-control-sm" style={{ width: 90 }} aria-label={`Return ${l.description}`} value={ret[l.id] || ""} onChange={(e) => setRet({ ...ret, [l.id]: e.target.value })} /> : null) }]
                  : []),
              ]}
            />
            {returning ? (
              <div className="d-flex gap-2 mt-2">
                <input className="form-control" style={inputStyle} placeholder="Reason (damaged, wrong part ...)" aria-label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                <PrimaryButton onClick={postReturn}>Post return</PrimaryButton>
              </div>
            ) : null}
            {g.landed_costs.length ? (
              <>
                <h6 className="mt-3">Landed costs</h6>
                <DataTable rows={g.landed_costs} columns={[{ label: "Type", render: (c) => c.charge_type }, { label: "Description", render: (c) => c.description }, { label: "Spread by", render: (c) => c.allocation_basis.toLowerCase() }, { label: "Amount", num: true, render: (c) => fmtAmt(c.amount) }, { label: "Billed", num: true, render: (c) => fmtAmt(c.amount_billed) }]} />
              </>
            ) : null}
            {g.returns.length ? (
              <>
                <h6 className="mt-3">Returns to the supplier</h6>
                <DataTable rows={g.returns} columns={[{ label: "Return", render: (r) => r.return_number }, { label: "Date", render: (r) => formatDate(r.return_date) }, { label: "Reason", render: (r) => r.reason }, { label: "Value", num: true, render: (r) => fmtAmt(r.total) }]} />
                <div style={hint}>Goods already billed: a draft debit note is waiting in Payables for Finance to complete.</div>
              </>
            ) : null}
          </>
        ) : null}
      </LoadState>
      <FormDrawer
        show={landed}
        title="Add landed cost"
        initial={{ charge_type: "FREIGHT", amount: "", allocation_basis: "VALUE", description: "", supplier_id: null }}
        onHide={() => setLanded(false)}
        onDone={refresh}
        onSave={(clean) => stock.addLandedCost(id, { ...clean, amount: Number(clean.amount) })}
        fields={[
          { key: "charge_type", label: "Type", type: "select", options: ["FREIGHT", "DUTY", "INSURANCE", "CLEARING", "OTHER"].map((v) => ({ label: v.charAt(0) + v.slice(1).toLowerCase(), value: v })) },
          { key: "amount", label: "Amount (ledger currency)", type: "number", required: true },
          { key: "allocation_basis", label: "Spread by", type: "select", options: [{ label: "Value", value: "VALUE" }, { label: "Quantity", value: "QUANTITY" }] },
          { key: "description", label: "Description", max: 240 },
        ]}
      />
    </SlidePanel>
  );
}

export function GoodsReceipts() {
  const { data: s } = useInvSettings();
  const [q, setQ] = useState("");
  const { data, loading, error, reload } = useLoad(() => stock.listReceipts({ q: q || undefined }), [q]);
  const [receiving, setReceiving] = useState(false);
  const [open, setOpen] = useState(null);
  return (
    <div style={pageStyle}>
      <PageHeader
        title="Goods Receipts"
        subtitle="Goods received from suppliers (GRN)"
        actions={s && s.can.purchase && s.purchasing_enabled && s.allow_direct_grn ? <PrimaryButton onClick={() => setReceiving(true)}>Receive without an order</PrimaryButton> : null}
      />
      <input className="form-control mb-2" style={{ ...inputStyle, maxWidth: 300 }} placeholder="Search receipt, supplier, order, delivery note" aria-label="Search" value={q} onChange={(e) => setQ(e.target.value)} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No goods received yet" text="Receive goods from an approved purchase order, or directly here." />}
          columns={[
            { label: "Receipt", render: (r) => <b>{r.grn_number}</b> },
            { label: "Date", render: (r) => formatDate(r.receipt_date) },
            { label: "Supplier", render: (r) => r.supplier_name },
            { label: "Order", render: (r) => r.po_number || "" },
            { label: "Delivery note", render: (r) => r.supplier_delivery_note || "" },
            { label: "Warehouse", render: (r) => r.warehouse_code || "" },
            { label: "Value", num: true, render: (r) => fmtAmt(r.value) },
          ]}
        />
      </LoadState>
      {receiving ? <ReceivePanel onHide={() => setReceiving(false)} onDone={(id) => { reload(); if (id) setOpen(id); }} /> : null}
      {open && s ? <ReceiptView id={open} s={s} onHide={() => setOpen(null)} onChanged={reload} /> : null}
    </div>
  );
}
